if (!customElements.get('compare-modal')) {
  class CompareModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.table = this.querySelector('[data-compare-table]');
      this.headerRow = this.querySelector('[data-compare-header-row]');
      this.tableBody = this.querySelector('[data-compare-table-body]');
      this.diffToggle = this.querySelector('[data-compare-diff-toggle]');
      this.diffToggleCell = this.querySelector('[data-compare-diff-toggle-cell]');
      this.suppressReload = false;

      this.diffToggle.addEventListener('change', this.updateRowVisibility.bind(this));
      this.querySelector('[data-compare-modal-close]').addEventListener('click', this.close.bind(this));
      this.dialog.addEventListener('cancel', this.close.bind(this));

      // A click lands directly on the <dialog> element itself (not a
      // descendant) only when it's on the backdrop or the dialog's own
      // padding — never when it's on something inside .compare-modal__inner.
      this.dialog.addEventListener('click', function (event) {
        if (event.target === this.dialog) this.close();
      }.bind(this));

      this.unsubscribe = subscribe('compare:change', function () {
        if (this.dialog.open && !this.suppressReload) this.loadColumns();
      }.bind(this));
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    open() {
      this.dialog.showModal();
      bodyScroll.lock(this.dialog);
      this.loadColumns();
    }

    close() {
      this.dialog.close();
      bodyScroll.unlock(this.dialog);
    }

    // Removes every previously-inserted product cell (header th + each
    // row's td), leaving the static label rows untouched.
    clearColumns() {
      this.querySelectorAll('[data-handle]').forEach(function (el) {
        el.remove();
      });
    }

    loadColumns() {
      var list = getCompareList();

      this.clearColumns();
      // Drives the mobile spec-row grid (label on its own line, then the
      // values sharing the line below it evenly) — needs the actual
      // count since it varies (2 or 3 products).
      // Set on the <table> itself (not tbody) since custom properties
      // only inherit downward — .compare-table's own min-width needs to
      // read this too, and a table is an ancestor of its tbody, not a
      // descendant, so setting it there would never reach the table.
      this.table.style.setProperty('--compare-column-count', list.length);
      // The toggle row's single <td> needs to span the label column plus
      // however many product columns are currently selected.
      this.diffToggleCell.colSpan = list.length + 1;

      // Rows keep whatever hidden state pruneEmptyRows last left them in
      // (they're static, never recreated) — unhide everything up front so
      // every row's loading skeleton is actually visible this time,
      // rather than staying hidden from a previous comparison's result.
      this.tableBody.querySelectorAll('tr[data-spec-key]').forEach(function (row) {
        row.hidden = false;
      });

      // Wait for every column to settle before pruning empty rows even
      // once, rather than re-pruning after each one individually — doing
      // it per-column made rows repeatedly hide/reappear as products
      // resolved out of order, visibly resizing the modal while it loaded.
      Promise.all(list.map(this.reserveAndLoadColumn.bind(this))).then(
        this.updateRowVisibility.bind(this)
      );
    }

    // Reserves a placeholder header cell and a placeholder value cell in
    // every spec row for this product, synchronously, before the fetch
    // even starts — so every row always has the same cell count/order
    // regardless of how many products are loading or which one resolves
    // first. The fetch then fills those exact placeholders in place.
    // Returns a promise that resolves once this column has settled
    // (success, unavailable, or error), never rejects.
    reserveAndLoadColumn(item) {
      var self = this;

      var headerCell = document.createElement('th');
      headerCell.className = 'compare-table__product compare-table__product--loading';
      headerCell.dataset.handle = item.handle;
      headerCell.setAttribute('aria-label', 'Laster ' + item.title);
      headerCell.innerHTML =
        '<div class="compare-column__body">' +
        '<div class="skeleton skeleton--image"></div>' +
        '<div class="skeleton skeleton--text"></div>' +
        '<div class="skeleton skeleton--text skeleton--text-narrow"></div>' +
        '</div>';
      this.headerRow.append(headerCell);

      var valueCells = {};

      this.tableBody.querySelectorAll('tr[data-spec-key]').forEach(function (row) {
        var cell = document.createElement('td');

        cell.dataset.handle = item.handle;
        cell.dataset.blank = 'true';
        cell.innerHTML = '<div class="skeleton skeleton--value"></div>';
        row.append(cell);
        valueCells[row.dataset.specKey] = cell;
      });

      return fetch(window.Shopify.routes.root + 'products/' + item.handle + '?view=compare')
        .then(function (response) {
          if (!response.ok) {
            if (response.status === 404) {
              self.markUnavailable(headerCell, valueCells, item.handle);
            } else {
              // Any other non-ok status (429, 500, 503, etc.) isn't
              // confirmed-gone evidence, so don't self-heal — just show a
              // neutral "couldn't load it" message.
              self.showUnavailable(headerCell, valueCells, 'Kunne ikke laste produktet');
            }

            return null;
          }

          return response.text();
        })
        .then(function (html) {
          if (html === null) return;

          var parsed = document.createElement('div');

          parsed.innerHTML = html;

          var fragmentTable = parsed.querySelector('[data-compare-fragment]');
          var realHeaderCell = fragmentTable && fragmentTable.querySelector('[data-compare-column]');

          if (!fragmentTable || !realHeaderCell) {
            // Malformed/unexpected response, not confirmed-gone: show a
            // neutral message but leave the handle in localStorage so a
            // later, successful reopen can still resolve it.
            self.showUnavailable(headerCell, valueCells, 'Kunne ikke laste produktet');
            return;
          }

          headerCell.replaceWith(realHeaderCell);

          fragmentTable.querySelectorAll('tbody tr[data-spec-key]').forEach(function (row) {
            var placeholderCell = valueCells[row.dataset.specKey];
            var realValueCell = row.querySelector('td');

            if (placeholderCell && realValueCell) {
              placeholderCell.replaceWith(realValueCell);
            }
          });
        })
        .catch(function () {
          // Thrown fetch exception (network blip, offline, aborted, etc.):
          // not confirmed-gone either, so leave the handle in localStorage.
          self.showUnavailable(headerCell, valueCells, 'Kunne ikke laste produktet');
        });
    }

    showUnavailable(headerCell, valueCells, message) {
      headerCell.className = 'compare-table__product compare-table__product--unavailable';
      headerCell.textContent = message;

      Object.keys(valueCells).forEach(function (key) {
        valueCells[key].dataset.blank = 'true';
        valueCells[key].textContent = '–';
      });
    }

    // Only call this where the failure is actual evidence the product no
    // longer resolves (a confirmed 404). Any other non-ok status, a thrown
    // fetch exception, or a malformed response is not evidence of that, so
    // it must not drop a still-valid product out of the user's selection.
    markUnavailable(headerCell, valueCells, handle) {
      this.showUnavailable(headerCell, valueCells, 'Produktet er ikke lenger tilgjengelig');

      // Self-heal localStorage without letting our own removal trigger a
      // full reload that would immediately wipe the message we just set.
      this.suppressReload = true;

      try {
        removeFromCompare(handle);
      } finally {
        this.suppressReload = false;
      }
    }

    // Hides any spec row that's blank across every currently-loaded
    // column (a spec no compared product actually has isn't worth a
    // row), and — when the "vis kun forskjeller" toggle is checked —
    // also hides rows where every loaded column shows the exact same
    // value, since there's nothing to compare there either. Runs fresh
    // after every column settles and whenever the toggle changes, so it
    // self-corrects as more columns load — a row only stays hidden once
    // every value cell in it agrees.
    updateRowVisibility() {
      var onlyDifferences = this.diffToggle.checked;

      this.tableBody.querySelectorAll('tr[data-spec-key]').forEach(function (row) {
        var valueCells = row.querySelectorAll('td[data-blank]');

        if (valueCells.length === 0) {
          row.hidden = true;
          return;
        }

        var texts = Array.prototype.map.call(valueCells, function (cell) {
          return cell.textContent.trim();
        });
        var allBlank = Array.prototype.every.call(valueCells, function (cell) {
          return cell.dataset.blank === 'true';
        });
        var allSame = texts.every(function (text) {
          return text === texts[0];
        });

        row.hidden = onlyDifferences ? allSame : allBlank;
      });
    }
  }

  customElements.define('compare-modal', CompareModal);
}
